import React, { useCallback } from 'react';
import { useMyOrganizationClientInvitations } from '../../../hooks/api/useMyOrganizationClientInvitations';
import { AnimateLoadedContent } from '../../v2BaseComponents/Loader';
import OrganizationClientInvitation from './OrganizationClientInvitation';
import useMedicalProfiles from '../../../hooks/api/useMedicalProfiles';
import { AcceptanceArgs } from './types';
import { APITypes } from '../../../primary-record-types';
import { useNestedNavigation } from '../../../utils/appNavigationNames';

export type OrganizationInvitationGateOwnProps = {
    renderContent: () => React.ReactNode;
};

export type OrganizationInvitationGateProps = OrganizationInvitationGateOwnProps;

export const RePromptOrganizationInvitationContext: React.Context<() => void> = React.createContext(() => {});

const OrganizationInvitationGate: React.FC<OrganizationInvitationGateProps> = (props) => {
    const [hasUserDismissed, setHasUserDismissed] = React.useState(false);
    const { nestedProfileNavigation } = useNestedNavigation();
    const { isLoadingClientInvitations, clientInvitations, respondToInvitation, isRespondingToInvitation } =
        useMyOrganizationClientInvitations({
            onRespondToInvitation: ({ request, response }) => {
                if (response.response === 'accept') {
                    nestedProfileNavigation('org-invite-accept-success', {
                        organizationId: request.organizationId,
                        clientProfileId: response.medicalProfileId,
                    });
                } else {
                    nestedProfileNavigation('root', undefined);
                }
            },
        });
    const { isLoadingProfiles, medicalProfiles } = useMedicalProfiles();
    const invitation = isLoadingClientInvitations ? null : clientInvitations[0] || null;

    const handleAcceptInvitation = ({ profile, permissions, fileInputs }: AcceptanceArgs) => {
        if (!invitation) return;

        const response: APITypes.organizationService.OrganizationClientInvitationAcceptResponse =
            invitation.provisionedMedicalProfileId
                ? {
                      response: 'accept',
                      medicalProfileId: invitation.provisionedMedicalProfileId,
                      appPermissions: permissions,
                      fileInputs,
                  }
                : profile.type === 'existing'
                ? { response: 'accept', medicalProfileId: profile.profileId, appPermissions: permissions, fileInputs }
                : {
                      response: 'accept',
                      appPermissions: permissions,
                      createMedicalProfileRequest: {
                          contactDetails: {
                              firstName: profile.profileInfo.firstName,
                              lastName: profile.profileInfo.lastName,
                              preferredFirstName: profile.profileInfo.firstName,
                              dob: profile.profileInfo.dateOfBirth,
                              entityType: 'medical-profile',
                              contactType: 'entity-contact',
                          },
                          emergencyContactDetails: [],
                          avatar: { type: 'not-modified' },
                      },
                      fileInputs,
                  };

        respondToInvitation({
            organizationId: invitation.organization.organizationId,
            invitationId: invitation.organizationClientInvitationId,
            response,
        });
    };

    const handleRejectInvitation = () => {
        if (!invitation) return;

        respondToInvitation({
            organizationId: invitation.organization.organizationId,
            invitationId: invitation.organizationClientInvitationId,
            response: { response: 'reject' },
        });
    };

    const handleRePromptInvitation = useCallback(() => {
        setHasUserDismissed(false);
    }, []);

    return (
        <RePromptOrganizationInvitationContext.Provider value={handleRePromptInvitation}>
            <AnimateLoadedContent isLoading={isLoadingClientInvitations}>
                {() => (
                    <>
                        {invitation && !hasUserDismissed ? (
                            <AnimateLoadedContent isLoading={isLoadingProfiles || isRespondingToInvitation}>
                                {() => (
                                    <OrganizationClientInvitation
                                        clientInvitation={invitation}
                                        profiles={medicalProfiles}
                                        onAccept={handleAcceptInvitation}
                                        onReject={handleRejectInvitation}
                                        onDismiss={() => setHasUserDismissed(true)}
                                    />
                                )}
                            </AnimateLoadedContent>
                        ) : (
                            props.renderContent()
                        )}
                    </>
                )}
            </AnimateLoadedContent>
        </RePromptOrganizationInvitationContext.Provider>
    );
};

export default React.memo(OrganizationInvitationGate);
